import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import Sidebar from '../components/admin/Sidebar';
import TopNavbar from '../components/admin/TopNavbar';
import DateRangeSelector from '../components/admin/analytics/DateRangeSelector';
import OverviewStatsCards from '../components/admin/analytics/OverviewStatsCards';
import OrdersByHourChart from '../components/admin/analytics/OrdersByHourChart';
import TopSellingItems from '../components/admin/analytics/TopSellingItems';
import CustomerAnalytics from '../components/admin/analytics/CustomerAnalytics';
import PaymentMethods from '../components/admin/analytics/PaymentMethods';
import PerformanceIndicators from '../components/admin/analytics/PerformanceIndicators';
import './AnalyticsDashboard.css';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [adminSession, setAdminSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dateRange, setDateRange] = useState('last30Days');
  const [customDateRange, setCustomDateRange] = useState({ from: '', to: '' });
  const [overviewStats, setOverviewStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    avgOrderValue: 0,
    customerCount: 0,
    ordersChange: 0,
    revenueChange: 0,
    aovChange: 0,
    newCustomers: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const session = localStorage.getItem('adminSession') || sessionStorage.getItem('adminSession');
    if (!session) {
      navigate('/admin');
      return;
    }
    setAdminSession(JSON.parse(session));
  }, [navigate]);

  useEffect(() => {
    if (adminSession) {
      fetchAnalytics();
    }
  }, [dateRange, customDateRange, adminSession]);

  const getDateRange = () => {
    const today = new Date();
    let startDate, endDate;

    if (dateRange === 'custom' && customDateRange.from && customDateRange.to) {
      startDate = new Date(customDateRange.from);
      endDate = new Date(customDateRange.to);
      endDate.setHours(23, 59, 59, 999);
    } else {
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      
      switch (dateRange) {
        case 'last7Days':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'last30Days':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 30);
          break;
        case 'last90Days':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 90);
          break;
        case 'lastYear':
          startDate = new Date(today);
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
        default:
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 30);
      }
    }
    startDate.setHours(0, 0, 0, 0);

    // Calculate previous period for comparison
    const periodLength = endDate - startDate;
    const previousEndDate = new Date(startDate);
    previousEndDate.setMilliseconds(previousEndDate.getMilliseconds() - 1);
    const previousStartDate = new Date(previousEndDate);
    previousStartDate.setTime(previousStartDate.getTime() - periodLength);

    return { startDate, endDate, previousStartDate, previousEndDate };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { startDate, endDate, previousStartDate, previousEndDate } = getDateRange();

      // Fetch current period orders
      const { data: currentOrders, error: ordersError } = await supabase
        .from('orders')
        .select('id, total, customer_id, created_at')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (ordersError) throw ordersError;

      // Fetch previous period orders for comparison
      const { data: previousOrders } = await supabase
        .from('orders')
        .select('id, total, customer_id, created_at')
        .gte('created_at', previousStartDate.toISOString())
        .lte('created_at', previousEndDate.toISOString());

      // Calculate current period stats
      const totalOrders = currentOrders?.length || 0;
      const totalRevenue = (currentOrders || []).reduce((sum, order) => sum + parseFloat(order.total || 0), 0);
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      // Calculate previous period stats
      const previousTotalOrders = previousOrders?.length || 0;
      const previousTotalRevenue = (previousOrders || []).reduce((sum, order) => sum + parseFloat(order.total || 0), 0);
      const previousAvgOrderValue = previousTotalOrders > 0 ? previousTotalRevenue / previousTotalOrders : 0;

      // Calculate changes
      const ordersChange = previousTotalOrders > 0 
        ? (((totalOrders - previousTotalOrders) / previousTotalOrders) * 100).toFixed(0)
        : 0;
      const revenueChange = previousTotalRevenue > 0
        ? (((totalRevenue - previousTotalRevenue) / previousTotalRevenue) * 100).toFixed(0)
        : 0;
      const aovChange = previousAvgOrderValue > 0
        ? (((avgOrderValue - previousAvgOrderValue) / previousAvgOrderValue) * 100).toFixed(0)
        : 0;

      // Fetch unique customers in current period
      const uniqueCustomerIds = new Set(
        (currentOrders || []).map(order => order.customer_id).filter(id => id !== null)
      );
      const customerCount = uniqueCustomerIds.size;

      // Fetch new customers (customers created in current period)
      const { count: newCustomersCount } = await supabase
        .from('customers')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      setOverviewStats({
        totalOrders,
        totalRevenue,
        avgOrderValue,
        customerCount,
        ordersChange: parseFloat(ordersChange),
        revenueChange: parseFloat(revenueChange),
        aovChange: parseFloat(aovChange),
        newCustomers: newCustomersCount || 0
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!adminSession) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="analytics-dashboard">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="analytics-main">
        <TopNavbar 
          onMenuClick={() => setSidebarOpen(true)}
          adminName={adminSession.username || adminSession.email}
        />
        
        <main className="analytics-content">
          <div className="page-header">
            <div>
              <h1 className="page-title">Reports & Analytics</h1>
              <nav className="breadcrumb">
                <span>Dashboard</span>
                <span className="breadcrumb-separator">/</span>
                <span>Reports</span>
              </nav>
            </div>
          </div>

          <DateRangeSelector
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            customDateRange={customDateRange}
            onCustomDateRangeChange={setCustomDateRange}
          />

          <OverviewStatsCards stats={overviewStats} />

          <div className="charts-section">
            <div className="charts-left">
              <OrdersByHourChart />
            </div>
            <div className="charts-right">
              <TopSellingItems />
            </div>
          </div>

          <CustomerAnalytics />

          <PaymentMethods />

          <PerformanceIndicators />
        </main>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;

